import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import { eq } from 'tio-ui/utilities';
import type { ComponentLike } from '@glint/template';
import { getIconTokens, getValueTextClass } from 'tio-reimagined/color-tokens';
import type { ReimaginedColor } from 'tio-reimagined/color-tokens';

/**
 * Color theme options for the stat card
 */
export type StatCardColor = ReimaginedColor;

/**
 * StatCard - A metric display card for dashboards and summary sections
 *
 * Displays a key statistic with icon, label, value, and optional subtext or trend.
 * Used throughout the reimagined experience for consistent metric presentation.
 *
 * Supports two layouts via `@compact`:
 * - **Default (false):** Icon with colored background circle, p-5 padding
 * - **Compact (true):** Inline icon without background, p-4 padding — ideal for dense grids
 *
 * @example Default layout
 * ```gts
 * <StatCard
 *   @icon={{PhCurrencyDollar}}
 *   @label="Annual Benefit"
 *   @value="$5,250"
 *   @subtext="Tax-free under Section 127"
 *   @color="emerald"
 * />
 * ```
 *
 * @example Compact layout (replaces SummaryStatCard)
 * ```gts
 * <StatCard
 *   @compact={{true}}
 *   @icon={{PhUsers}}
 *   @label="In Pipeline"
 *   @value={{85}}
 *   @suffix="mo"
 *   @valueColor="violet"
 *   @trend="up"
 *   @trendText="+12% YoY"
 * />
 * ```
 *
 * @example With trend indicator
 * ```gts
 * <StatCard
 *   @icon={{PhTrendUp}}
 *   @label="Career Score"
 *   @value="92"
 *   @trend="up"
 *   @trendText="↑ 8% this quarter"
 *   @color="violet"
 * />
 * ```
 */
interface StatCardSignature {
  Args: {
    /** Phosphor icon component to display (optional in compact mode) */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display (formatted string, e.g. "$42,000") */
    value: string | number;
    /** Secondary text below the value */
    subtext?: string;
    /**
     * Additional secondary line used primarily in compact layouts.
     *
     * Rendered separately from @subtext (typically in the trend/secondary line
     * area) and only shown when @trendText is not provided. For simple secondary
     * copy directly below the value, prefer using @subtext.
     */
    secondaryText?: string;
    /** Optional suffix appended to the value (e.g., "%", "mo") */
    suffix?: string;
    /** Color theme for icon background (default layout) */
    color?: StatCardColor;
    /** Color theme for the value text (overrides default gray) */
    valueColor?: StatCardColor;
    /** Color theme for the icon (compact mode, overrides @color) */
    iconColor?: StatCardColor;
    /** Optional CSS class for value styling (e.g. for colored values) */
    valueClass?: string;
    /** Trend direction — colors the trend text (green/red/gray) */
    trend?: 'up' | 'down' | 'stable';
    /** Trend label text displayed below the value */
    trendText?: string;
    /** Compact layout: smaller padding, inline icon, no background circle */
    compact?: boolean;
  };
  Blocks: {
    /** Optional extra content below the value (e.g., progress bar) */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const getColorClasses = (color?: StatCardColor) => getIconTokens(color, '50');

const getValueColorClass = (valueColor?: StatCardColor, valueClass?: string) => {
  if (valueClass) return valueClass;
  if (valueColor) return getValueTextClass(valueColor);
  return 'text-gray-900';
};

const trendColorClass = (trend?: 'up' | 'down' | 'stable') => {
  if (trend === 'down') return 'text-red-600 dark:text-red-400';
  if (trend === 'stable') return 'text-gray-500';
  return 'text-green-600 dark:text-green-400';
};

const getIconColorClass = (iconColor?: StatCardColor, color?: StatCardColor) => {
  const tokens = getIconTokens(iconColor ?? color, '50');
  return tokens.iconText;
};

const StatCard: TOC<StatCardSignature> = <template>
  {{#let
    (getColorClasses @color) (getValueColorClass @valueColor @valueClass)
    as |colors valClass|
  }}
    <div
      data-test-stat-card
      class="bg-surface-card rounded-lg shadow-sm border border-default overflow-hidden
        {{if @compact 'p-4' 'p-5'}}"
      role="group"
      aria-label={{@label}}
      ...attributes
    >
      {{#if @compact}}
        {{! Compact layout: inline icon with label }}
        {{#if @icon}}
          <div class="flex items-center gap-2 mb-2">
            {{#let @icon as |Icon|}}
              <Icon @size={{18}} class="{{getIconColorClass @iconColor @color}}" />
            {{/let}}
            <Text data-test-stat-card-label @intent="muted" @size="sm">{{@label}}</Text>
          </div>
        {{else}}
          <Text data-test-stat-card-label class="text-gray-500 text-sm mb-1">{{@label}}</Text>
        {{/if}}
      {{else}}
        {{! Default layout: icon with background circle }}
        <div class="flex items-center gap-3 mb-3">
          {{#if @icon}}
            <div class="p-2 {{colors.iconBg}} rounded-lg {{colors.iconText}}">
              {{#let @icon as |Icon|}}
                <Icon @size={{24}} />
              {{/let}}
            </div>
          {{/if}}
          <Text data-test-stat-card-label @intent="muted" class="text-sm">{{@label}}</Text>
        </div>
      {{/if}}
      <div data-test-stat-card-value class="text-2xl font-bold truncate min-w-0 {{valClass}}">
        {{@value}}{{if @suffix @suffix}}
      </div>
      {{#if @subtext}}
        <Text data-test-stat-card-subtext @intent="muted" class="text-xs mt-1">{{@subtext}}</Text>
      {{/if}}
      {{#if @trendText}}
        {{#if @compact}}
          <div class="flex items-center gap-1 mt-1">
            {{#if (eq @trend "up")}}
              <svg
                role="presentation"
                class="w-3.5 h-3.5 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              ><path
                  role="presentation"
                  fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
                  clip-rule="evenodd"
                /></svg>
            {{else if (eq @trend "down")}}
              <svg
                role="presentation"
                class="w-3.5 h-3.5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              ><path
                  role="presentation"
                  fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                /></svg>
            {{/if}}
            <Text
              data-test-stat-card-trend
              @size="sm"
              class="{{trendColorClass @trend}}"
            >{{@trendText}}</Text>
          </div>
        {{else}}
          <span
            data-test-stat-card-trend
            class="text-xs font-medium mt-1 inline-block {{trendColorClass @trend}}"
          >
            {{@trendText}}
          </span>
        {{/if}}
      {{else if @secondaryText}}
        <Text
          data-test-stat-card-secondary
          @size="sm"
          class="text-gray-500"
        >{{@secondaryText}}</Text>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-2">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default StatCard;

export type { StatCardSignature };
